import { UserRound, CircleDot, FolderKanban, FolderInput, Eye, Trash2 } from 'lucide-react';
import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { resolveProjectColor } from '../lib/projectColors';

/**
 * The bulk-action entries applied to the WHOLE current selection — the same verbs
 * as a row's single-item ⋱ menu minus Edit. Rendered INSIDE a row's
 * DropdownMenuContent when that row is part of a multi-selection, so acting on any
 * selected task acts on all of them (Finder-style). Each action is optional; a
 * surface passes only the handlers that apply (e.g. the board omits "Assign to"
 * when a private card is in the selection). Choosing an option APPLIES it to every
 * selected task (the selection can hold a mix of current values).
 */
export interface BulkActionsProps {
  /** Assign to — members roster; null clears the assignee. */
  members?: Array<{ principalId: string; label: string }>;
  onAssign?: (assigneePrincipalId: string | null) => void;
  /** Set status — the statuses allowed for this selection/surface. */
  statusOptions?: Array<{ value: string; label: string }>;
  onSetStatus?: (status: string) => void;
  /** Set project — null clears it. */
  projects?: Array<{ projectId: string; name: string; color: string | null }>;
  onSetProject?: (projectId: string | null) => void;
  /** Move to section (list) or board column — null = Unsectioned/Unsorted. */
  sectionOptions?: Array<{ sectionId: string; name: string; depth: number }>;
  canMoveToUnsorted?: boolean;
  onSetSection?: (sectionId: string | null) => void;
  /** Send for review — enabled only when every selected task is review-ready. */
  reviewerTargets?: Array<{ principalId: string; label: string }>;
  reviewReady?: boolean;
  reviewBlockReason?: string;
  onSendForReview?: (reviewerPrincipalId: string | null) => void;
  /** Move to list… — opens a surface-managed dialog. */
  onMoveToList?: () => void;
  /** Delete — the surface confirms ("Delete N tasks?"). */
  onDelete?: () => void;
}

export function BulkActionsMenuItems({
  members,
  onAssign,
  statusOptions,
  onSetStatus,
  projects,
  onSetProject,
  sectionOptions,
  canMoveToUnsorted = true,
  onSetSection,
  reviewerTargets,
  reviewReady = false,
  reviewBlockReason,
  onSendForReview,
  onMoveToList,
  onDelete,
}: BulkActionsProps) {
  const hasAssign = !!onAssign && !!members && members.length > 0;
  const hasStatus = !!onSetStatus && !!statusOptions && statusOptions.length > 0;
  const hasProject = !!onSetProject && !!projects && projects.length > 0;
  const hasSection = !!onSetSection && !!sectionOptions;
  const hasReview = !!onSendForReview && !!reviewerTargets && reviewerTargets.length > 0;

  return (
    <>
      {hasAssign && (
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <UserRound className="mr-2 h-3.5 w-3.5" />
            Assign to
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem onClick={() => onAssign!(null)}>Unassigned</DropdownMenuItem>
            {members!.map((m) => (
              <DropdownMenuItem key={m.principalId} onClick={() => onAssign!(m.principalId)}>
                {m.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      )}
      {hasStatus && (
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <CircleDot className="mr-2 h-3.5 w-3.5" />
            Set status
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {statusOptions!.map((s) => (
              <DropdownMenuItem key={s.value} onClick={() => onSetStatus!(s.value)}>
                {s.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      )}
      {hasProject && (
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <FolderKanban className="mr-2 h-3.5 w-3.5" />
            Set project
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem onClick={() => onSetProject!(null)}>No project</DropdownMenuItem>
            {projects!.map((p) => (
              <DropdownMenuItem key={p.projectId} onClick={() => onSetProject!(p.projectId)}>
                <span
                  className={`mr-2 h-2 w-2 flex-shrink-0 rounded-full ${resolveProjectColor(p.color).dot}`}
                />
                {p.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      )}
      {hasSection && (
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <FolderInput className="mr-2 h-3.5 w-3.5" />
            Move to section
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="max-h-72 overflow-y-auto">
            {canMoveToUnsorted && (
              <DropdownMenuItem onClick={() => onSetSection!(null)}>Unsectioned</DropdownMenuItem>
            )}
            {sectionOptions!.map((s) => (
              <DropdownMenuItem key={s.sectionId} onClick={() => onSetSection!(s.sectionId)}>
                <span style={{ paddingLeft: `${s.depth * 0.75}rem` }}>{s.name}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      )}
      {hasReview && (
        <DropdownMenuSub>
          <DropdownMenuSubTrigger
            disabled={!reviewReady}
            title={!reviewReady ? reviewBlockReason : undefined}
          >
            <Eye className="mr-2 h-3.5 w-3.5" />
            Send for review
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem onClick={() => onSendForReview!(null)}>
              All reviewers
            </DropdownMenuItem>
            {reviewerTargets!.map((t) => (
              <DropdownMenuItem key={t.principalId} onClick={() => onSendForReview!(t.principalId)}>
                {t.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      )}
      {onMoveToList && (
        <DropdownMenuItem onClick={onMoveToList}>
          <FolderInput className="mr-2 h-3.5 w-3.5" />
          Move to list…
        </DropdownMenuItem>
      )}
      {onDelete && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={onDelete}>
            <Trash2 className="mr-2 h-3.5 w-3.5" />
            Delete
          </DropdownMenuItem>
        </>
      )}
    </>
  );
}
